import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Checkbox, FormControlLabel } from "@mui/material";
import { getEdgeOta, revokeEdgeOta, setEdgeOtaCritical } from "../../api/otaTenantApi";
import OtaApprovalDialog from "./OtaApprovalDialog";
import ChannelDialog from "./ChannelDialog";
import { errDetail, releaseKind } from "./otaHelpers";
import { formatDate } from "../../utils/i18nFormat";

const fmt = (s) => formatDate(s);
const row = { display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--border)", alignItems: "center" };

// OTA tab of the edge detail page. Only rendered for tenant admins / superadmin (backend requires it).
export default function OtaCard({ edge }) {
  const { t } = useTranslation();
  const token = useSelector((s) => s.session?.accessToken);
  const [ota, setOta] = useState(null);
  const [dlg, setDlg] = useState(null); // "approve" | "channel"
  const one = [{ id: edge.id, name: edge.name ?? edge.edgeId }];

  const load = useCallback(() => {
    getEdgeOta(token, edge.id).then(setOta).catch((e) => toast.error(errDetail(e, t("edges.ota.card.loadFailed"))));
  }, [token, edge.id, t]);
  useEffect(load, [load]);

  const toggleCritical = async () => {
    try {
      await setEdgeOtaCritical(token, edge.id, !ota.require_local_approval);
      load();
    } catch (e) { toast.error(errDetail(e, t("edges.ota.card.saveFailed"))); }
  };
  const revoke = async () => {
    if (!window.confirm(t("edges.ota.card.confirmRevoke"))) return;
    try {
      await revokeEdgeOta(token, edge.id, ota.release.id);
      toast.success(t("edges.ota.card.revoked"));
      load();
    } catch (e) { toast.error(errDetail(e, t("edges.ota.card.revokeFailed"))); }
  };

  if (!ota) return <div style={{ color: "var(--text-muted)" }}>{t("edges.loading")}</div>;
  const { release, approval } = ota;
  return (
    <div>
      <div style={row}>
        <b style={{ width: 180 }}>{t("edges.ota.card.channel")}</b><span style={{ flex: 1 }}>{ota.channel || "stable"}</span>
        <Button size="small" onClick={() => setDlg("channel")}>{t("edges.ota.card.change")}</Button>
      </div>
      <div style={row}>
        <b style={{ width: 180 }}>{t("edges.ota.card.critical")}</b>
        <FormControlLabel sx={{ flex: 1, m: 0 }} control={<Checkbox checked={ota.require_local_approval} onChange={toggleCritical} />} label={t("edges.ota.card.requireLocal")} />
      </div>
      <div style={row}>
        <b style={{ width: 180 }}>{t("edges.ota.card.offered")}</b>
        <span style={{ flex: 1 }}>{release ? `${release.version} · ${releaseKind(release)}` : t("edges.ota.card.noUpdate")}</span>
        {release && !approval && <Button size="small" variant="contained" onClick={() => setDlg("approve")}>{t("edges.ota.card.approve")}</Button>}
      </div>
      {release && approval && (
        <div style={row}>
          <b style={{ width: 180 }}>{t("edges.ota.card.approval")}</b>
          <span style={{ flex: 1 }}>{approval.approved_by} · {fmt(approval.window_start)} – {fmt(approval.window_end)}</span>
          <Button size="small" color="error" onClick={revoke}>{t("edges.ota.card.revoke")}</Button>
        </div>
      )}
      <OtaApprovalDialog open={dlg === "approve"} edges={one} onClose={(ch) => { setDlg(null); if (ch) load(); }} />
      <ChannelDialog open={dlg === "channel"} edges={one} onClose={(ch) => { setDlg(null); if (ch) load(); }} />
    </div>
  );
}
